/**
 * Render every phone number in a post as an explicit `tel:` link.
 *
 * Why this exists: some iOS browsers turn on WKWebView phone-number data
 * detection for every page, and it ignores the page's
 * `<meta name="format-detection" content="telephone=no">` (Firefox iOS sets
 * `dataDetectorTypes = [.phoneNumber]` in its WKWebView configuration, and
 * Ecosia iOS is built from Firefox iOS). The detector wraps a bare number such
 * as a park's booking line in `<a href="tel:">` before React hydrates, React
 * finds a link where it rendered text (React error 418), and it throws the
 * article away to render it again. A detector leaves text that is already
 * inside a link alone, so a number we link ourselves gives it nothing to
 * wrap. This runs on the server and in the browser alike, so both render the
 * same link.
 *
 * The pattern takes the shapes our posts use: North American
 * (1-800-689-9025), Australian (1800 111 111, (08) 9947 8000) and New Zealand
 * (09 366 2000). It must stay free of regex lookbehind, which throws on
 * Safari before 16.4 (see remarkGfmNoAutolink.js), so the character before a
 * match is checked in code.
 */

// Three or more digit groups, so a year range (2023-2025) is not a number.
const PHONE = /\+?(?:\(\d{2,4}\)|\d{1,4})(?:[ .-]\d{2,4}){2,4}(?!\d)/g;

// Text under these is code, raw HTML, or already a link.
const SKIP = new Set(["link", "linkReference", "inlineCode", "code", "html"]);

function isPhone(match, before) {
  if (before && /[\w.+-]/.test(before)) return false;
  const digits = match.replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15) return false;
  // A date (2026-10-08) or a range (27.50-31) ends in a short group.
  return /\d{3,}$/.test(match);
}

function splitText(node) {
  const { value } = node;
  const parts = [];
  let last = 0;
  for (const match of value.matchAll(PHONE)) {
    const [number] = match;
    if (!isPhone(number, value[match.index - 1])) continue;
    if (match.index > last) {
      parts.push({ type: "text", value: value.slice(last, match.index) });
    }
    const plus = number.startsWith("+") ? "+" : "";
    parts.push({
      type: "link",
      url: `tel:${plus}${number.replace(/\D/g, "")}`,
      children: [{ type: "text", value: number }],
    });
    last = match.index + number.length;
  }
  if (last === 0) return [node];
  if (last < value.length) {
    parts.push({ type: "text", value: value.slice(last) });
  }
  return parts;
}

function linkPhones(parent) {
  parent.children = parent.children.flatMap((child) => {
    if (child.type === "text") return splitText(child);
    if (child.children && !SKIP.has(child.type)) linkPhones(child);
    return [child];
  });
}

export default function remarkTelLinks() {
  return (tree) => linkPhones(tree);
}
